import { createMiddleware } from 'redux-beacon';
import googleTagManager from '@redux-beacon/google-tag-manager';
import { makeAction } from '@finn-no/redux-actions';
import isMobile from 'shared/util/mobile.js';

const screenType = isMobile() ? 'small' : 'large';

const resetDataLayer = () => {
    if (window.dataLayer !== undefined) {
        // Must reset the dataLayer variable even when GTM is not loaded to make it work in tests
        window.dataLayer.length = 0;

        if (window.google_tag_manager !== undefined) {
            const gtmContainerReg = /GTM-/i;

            for (const gtmKey of Object.keys(window.google_tag_manager)) {
                if (gtmContainerReg.test(gtmKey)) {
                    window.google_tag_manager[gtmKey]?.dataLayer?.reset?.(); // eslint-disable-line no-unused-expressions
                }
            }
        }
    }
};

function mapTripType(tripType, numberOfLegs) {
    if (tripType === 'openjaw' || tripType === 'multicity') {
        return `multicity(${numberOfLegs})`;
    }

    return tripType;
}

const offerData = (trip, request, filterState) => {
    const segments = trip.legs.flatMap(leg => leg.segments);
    const uniqueCarriers = [...new Set(segments.map(segment => segment.marketingCarrierName))];

    return {
        originAirport: request.origin,
        originCity: request.requestedOriginCity,
        originCountry: request.requestedOriginCountry,
        destinationAirport: request.destination,
        destinationCity: request.requestedDestinationCity,
        destinationCountry: request.requestedDestinationCountry,
        departureDate: request.requestedDepartureDate,
        returnDate: request.requestedReturnDate,
        tripType: mapTripType(request.tripType, trip.legs.length),
        cabinType: request.cabinType,
        numberOfAdults: request.numberOfAdults,
        childrenAges: request.childAges,
        sortCriteria: filterState.sortCriteria,

        topPrice: trip.topPrice,
        topProvider: trip.topBrand,
        duration: trip.tripDuration,
        isCheapest: trip.cheapest,
        isFastest: trip.fastest,
        numberOfStops: segments.length - trip.legs.length,
        tonsOfCo2: trip.emission?.tonsOfCo2,
        providers: trip.offers.map(offer => offer.brand),
        carriers: uniqueCarriers,
    };
};

const searchPageLoaded = () => ({
    event: 'view',
    eventType: 'View',
    object: {
        id: 'flight-search',
        type: 'Page',
    },
    pageType: 'search',
    environment: 'prod',
    screenType,
});

const resultPageLoaded = ({ payload: request }) => ({
    event: 'view',
    eventType: 'View',
    object: {
        id: 'flight-result',
        type: 'Listing',

        originAirport: request.origin,
        originCity: request.requestedOriginCity,
        originCountry: request.requestedOriginCountry,
        destinationAirport: request.destination,
        destinationCity: request.requestedDestinationCity,
        destinationCountry: request.requestedDestinationCountry,
        departureDate: request.requestedDepartureDate,
        returnDate: request.requestedReturnDate,
        tripType: mapTripType(request.tripType),
        cabinType: request.cabinType,
        numberOfAdults: request.numberOfAdults,
        childrenAges: request.childAges,
    },
    pageType: 'list',
    environment: 'prod',
    screenType,
});

const expandFlightDetails = ({ payload: trip }, { request, filterState }) => {
    resetDataLayer();

    return {
        event: 'event',
        eventType: 'Engagement',
        object: {
            id: 'flight-detail',
            type: 'UIElement',

            ...offerData(trip, request, filterState),
        },
        gaEvent: {
            category: 'details',
            action: 'expand flight details',
        },
        environment: 'prod',
        screenType,
    };
};

const expandCompensationInfo = () => {
    resetDataLayer();

    return {
        event: 'event',
        eventType: 'Engagement',
        object: {
            id: 'co2-compensation-detail',
            type: 'UIElement',
        },
        gaEvent: {
            category: 'details',
            action: 'expand compensation info',
        },
        environment: 'prod',
        screenType,
    };
};

const filterChange = (action, state) => {
    resetDataLayer();

    const filterKeys = [
        'stops',
        'outbounddeparture',
        'inbounddeparture',
        'outboundarrival',
        'inboundarrival',
        'durationto',
        'cabintype',
        'departureairport',
        'arrivalairport',
        'stopoverairports',
        'priceto',
        'airlines',
    ];

    // Returns undefined if key not found in filterKeys
    const normalizeKey = key => filterKeys.find(fk => key.toLowerCase().includes(fk));

    const validFilters = Object.entries(action.payload)
        .map(([key, value]) => [normalizeKey(key), value])
        .filter(([key, value]) => key !== undefined && value !== undefined);

    const invertUncheckedAirlines = uncheckedAirlines =>
        state.filterData.airlines
            .map(airline => airline.value)
            .filter(iata => !uncheckedAirlines.includes(iata))
            .join(',');

    const mapValue = (key, value) => (key === 'airlines' ? invertUncheckedAirlines(value) : value);

    let filterKeyUsed;
    let value;

    if (validFilters.length === 0) {
        filterKeyUsed = 'n_a';
        value = 'n_a';
    } else {
        filterKeyUsed = validFilters[0][0];
        value = mapValue(filterKeyUsed, validFilters[0][1]);
    }

    const id = `filter-${filterKeyUsed}`;

    return {
        event: 'event',
        eventType: 'Engagement',
        object: {
            id,
            value,
            type: 'UIElement',
        },
        gaEvent: {
            category: 'search',
            action: 'changed filter',
            label: id,
        },
        environment: 'prod',
        screenType,
    };
};

const clickOutCompensationInfo = () => {
    resetDataLayer();

    return {
        event: 'event',
        eventType: 'Engagement',
        object: {
            id: 'clicked-on-climate-compensation-info',
            type: 'UIElement',
        },
        target: {
            id: 'blocket.se/information',
            type: 'Page',
        },
        gaEvent: {
            category: 'navigation',
            action: 'clicked on read more at blocket',
        },
        environment: 'prod',
        screenType,
    };
};

const clickOutOffer = (action, { request, filterState }) => {
    resetDataLayer();
    const { trip, offer, offerId } = action.payload;

    return {
        event: 'event',
        eventType: 'Engagement',
        object: {
            id: 'clicked-on-travel-partner',
            type: 'UIElement',

            ...offerData(trip, request, filterState),

            price: offer.priceAmount,
            provider: offer.brand,
            isAirline: offer.isAirline,
            position: offerId,
        },
        target: {
            id: offer.brand,
        },
        gaEvent: {
            category: 'lead',
            action: 'clicked flight partner link',
            label: offerId,
        },
        environment: 'prod',
        screenType,
    };
};

const clickOutBuyCompensation = ({ payload: trip }, { request, filterState }) => {
    resetDataLayer();

    return {
        event: 'event',
        eventType: 'Engagement',
        object: {
            // TODO is this id ok??
            id: 'clicked-on-climate-compensation-buy',
            type: 'UIElement',

            ...offerData(trip, request, filterState),
        },
        target: {
            id: 'go climate neutral',
            type: 'Page',
        },
        gaEvent: {
            category: 'external link',
            action: 'clicked buy climate compensation link',
        },
        environment: 'prod',
        screenType,
    };
};

const clickDestination = action => {
    resetDataLayer();

    return {
        event: 'event',
        eventType: 'Engagement',
        object: {
            id: 'image-navigation-bar',
            type: 'UIElement',
            name: action.payload.name,
            position: action.payload.index,
        },
        target: {
            id: action.payload.name,
            type: 'Page',
        },
        gaEvent: {
            category: 'navigation',
            action: 'clicked image navigation bar',
            label: action.payload.name,
            value: action.payload.index,
        },
        environment: 'prod',
        screenType,
    };
};

const clickCms = action => {
    resetDataLayer();

    return {
        event: 'event',
        eventType: 'Engagement',
        object: {
            id: 'image-cms',
            type: 'UIElement',
            name: action.payload.title,
            position: action.payload.index,
        },
        target: {
            id: action.payload.url,
            name: action.payload.title,
            type: 'Page',
        },
        gaEvent: {
            category: 'navigation',
            action: 'clicked content area',
            label: action.payload.title,
            value: action.payload.index,
        },
        environment: 'prod',
        screenType,
    };
};

const eventsMap = {
    SEARCH_PAGE_LOADED: searchPageLoaded,
    RESULT_PAGE_LOADED: resultPageLoaded,
    EXPAND_FLIGHT_DETAILS: expandFlightDetails,
    EXPAND_COMPENSATION_INFO: expandCompensationInfo,
    FILTER_CHANGE: filterChange,
    CLICK_OUT_BUY_COMPENSATION: clickOutBuyCompensation,
    CLICK_OUT_COMPENSATION_INFO: clickOutCompensationInfo,
    CLICK_OUT_OFFER: clickOutOffer,
    CLICK_DESTINATION: clickDestination,
    CLICK_CMS: clickCms,
};

const gtm = googleTagManager();
export const gtmMiddleware = createMiddleware(eventsMap, gtm);

export const gtmActions = {
    searchPageLoaded: makeAction('SEARCH_PAGE_LOADED', 'data'),
    resultPageLoaded: makeAction('RESULT_PAGE_LOADED', 'data'),
    expandFlightDetails: makeAction('EXPAND_FLIGHT_DETAILS', 'data'),
    expandCompensationInfo: makeAction('EXPAND_COMPENSATION_INFO', 'data'),
    filterChange: makeAction('FILTER_CHANGE', 'data'),
    clickOutOffer: makeAction('CLICK_OUT_OFFER', 'data'),
    clickOutBuyCompensation: makeAction('CLICK_OUT_BUY_COMPENSATION', 'data'),
    clickOutCompensationInfo: makeAction('CLICK_OUT_COMPENSATION_INFO', 'data'),
    clickDestination: makeAction('CLICK_DESTINATION', 'data'),
    clickCms: makeAction('CLICK_CMS', 'data'),
};
